import React from 'react';
import { NavLink, useParams, useNavigate, useLocation } from 'react-router-dom';
import { Home, BarChart2, ClipboardList, Settings, LogOut, Store, User, Wrench, ShoppingBag, Activity } from 'lucide-react';
import { useStore } from '../context/StoreContext';
import './Sidebar.css';

const Sidebar = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { stores, user, logout } = useStore();

    const allowedStores = React.useMemo(() => {
        if (!stores) return [];
        const restrictedAdmins = ['admin1', 'admin2', 'admin3'];
        if (user?.role === 'admin' && restrictedAdmins.includes(user?.id?.toLowerCase())) {
            return stores.filter(s => s.operatingType === 'direct');
        }
        return stores;
    }, [stores, user]);

    // URL에서 date 파라미터 추출
    const queryParams = new URLSearchParams(location.search);
    const currentDateParam = queryParams.get('date');
    const dateQuery = currentDateParam ? `?date=${currentDateParam}` : '';

    // 현재 선택된 매장 ID
    const currentStoreId = user?.role === 'manager' ? user.storeId : (id || '1');

    const handleLogout = async () => {
        await logout();
        navigate('/login', { replace: true });
    };

    const handleStoreChange = (e) => {
        const newId = e.target.value;
        // 현재 위치하고 있는 탭 타입을 유지하면서 매장만 변경 (날짜 정보도 유지)
        const currentPath = window.location.pathname;
        if (currentPath.includes('/sales')) navigate(`/store/${newId}/sales${dateQuery}`);
        else if (currentPath.includes('/inventory')) navigate(`/store/${newId}/inventory${dateQuery}`);
        else if (currentPath.includes('/facility')) navigate(`/store/${newId}/facility${dateQuery}`);
        else if (currentPath.includes('/order')) navigate(`/store/${newId}/order${dateQuery}`);
        else if (currentPath.includes('/settings')) navigate(`/store/${newId}/settings${dateQuery}`);
        else navigate(`/store/${newId}/sales${dateQuery}`);
    };

    return (
        <aside className="sidebar">
            <div className="sidebar-logo">
                <h2>주식회사 달</h2>
            </div>

            {user?.role === 'admin' && (
                <div className="store-selector-container">
                    <div className="store-selector-wrapper">
                        <Store size={18} className="store-icon" />
                        <select
                            className="store-select"
                            value={currentStoreId}
                            onChange={handleStoreChange}
                        >
                            {allowedStores.map(store => (
                                <option key={store.id} value={store.id}>
                                    {store.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}

            <nav className="nav-links">
                <NavLink to="/dashboard" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                    <Home size={20} /> {user?.role === 'admin' ? '전체 대시보드' : '매장 대시보드'}
                </NavLink>

                {user?.role === 'admin' && (
                    <>
                        <NavLink to="/analytics" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                            <BarChart2 size={20} /> 통계 분석
                        </NavLink>
                        <NavLink to="/vendors" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                            <User size={20} /> 거래처 관리
                        </NavLink>
                        {user?.id === 'admin' && (
                            <NavLink to="/access-logs" className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                                <Activity size={20} /> 접속 로그
                            </NavLink>
                        )}
                    </>
                )}

                <div className="nav-group-label">매장 관리</div>

                <NavLink to={`/store/${currentStoreId}/sales${dateQuery}`} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                    <BarChart2 size={20} /> 매출 관리
                </NavLink>
                <NavLink to={`/store/${currentStoreId}/inventory${dateQuery}`} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                    <ClipboardList size={20} /> 재고현황
                </NavLink>
                <NavLink to={`/store/${currentStoreId}/facility${dateQuery}`} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                    <Wrench size={20} /> 시설관리
                </NavLink>
                <NavLink to={`/store/${currentStoreId}/order${dateQuery}`} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                    <ShoppingBag size={20} /> 발주관리
                </NavLink>
                {user?.role === 'admin' && (
                    <NavLink to={`/store/${currentStoreId}/settings${dateQuery}`} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                        <Settings size={20} /> 매장 설정
                    </NavLink>
                )}
            </nav>

            <div className="user-profile-section" style={{ marginTop: 'auto', padding: '16px 0', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <div className="user-info-badge">
                    <User size={18} className="user-icon" />
                    <span className="user-label">
                        {user?.role === 'admin' ? (user?.id === 'admin4' ? '운영관리팀 모드' : `${user?.name} 모드`) : `${user?.name} 매니저`}
                    </span>
                </div>
            </div>

            <button onClick={handleLogout} className="nav-item logout-item" style={{ background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left' }}>
                <LogOut size={20} /> 로그아웃
            </button>
        </aside>
    );
};

export default Sidebar;

